import {
  PAYROLL_VOUCHER_STATUS_LABELS,
  PayrollVoucherStatusEnum,
} from "@/enum/payroll-voucher-status";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { PAYROLL_VOUCHER_RESPONSE_LABELS } from "../../utils/messages";
import { PayrollVoucherResponseModal } from "./payroll-voucher-response-modal";
import {
  DivergenceWithVoucher,
  PayrollVoucherSummary,
} from "./payroll-voucher-types";
import { StatusVariant } from "../../types";
import { StatusBadge } from "../shared/status-badge";
import { ExternalLink } from "lucide-react";

interface PayrollVoucherStatusBadgeProps {
  divergence: DivergenceWithVoucher;
}

const STATUS_VARIANT: Record<PayrollVoucherStatusEnum, StatusVariant> = {
  [PayrollVoucherStatusEnum.AWAITING_CLOSING]: "default",
  [PayrollVoucherStatusEnum.PENDING]: "default",
  [PayrollVoucherStatusEnum.APPROVED]: "approved",
  [PayrollVoucherStatusEnum.REJECTED]: "rejected",
};

const DECIDED_STATUSES: readonly PayrollVoucherStatusEnum[] = [
  PayrollVoucherStatusEnum.APPROVED,
  PayrollVoucherStatusEnum.REJECTED,
];

export function PayrollVoucherStatusBadge({
  divergence,
}: PayrollVoucherStatusBadgeProps): JSX.Element {
  const { handleModal } = useDialogModal();
  const voucher: PayrollVoucherSummary | null | undefined =
    divergence.payroll_voucher;

  if (!voucher || !voucher.status) {
    return (
      <StatusBadge variant="inactive">
        {PAYROLL_VOUCHER_RESPONSE_LABELS.NOT_SENT}
      </StatusBadge>
    );
  }

  const variant: StatusVariant = STATUS_VARIANT[voucher.status] ?? "default";
  const label: string =
    PAYROLL_VOUCHER_STATUS_LABELS[voucher.status] ?? voucher.status;
  const isClickable: boolean = DECIDED_STATUSES.includes(voucher.status);

  const badge: JSX.Element = (
    <StatusBadge variant={variant}>
      {label}
      {isClickable && <ExternalLink className="ml-1 h-3 w-3" />}
    </StatusBadge>
  );

  if (!isClickable) return badge;

  return (
    <button
      type="button"
      title={PAYROLL_VOUCHER_RESPONSE_LABELS.OPEN_RESPONSE}
      aria-label={PAYROLL_VOUCHER_RESPONSE_LABELS.OPEN_RESPONSE}
      onClick={() =>
        handleModal({
          isOpen: true,
          title: PAYROLL_VOUCHER_RESPONSE_LABELS.MODAL_TITLE,
          description: PAYROLL_VOUCHER_RESPONSE_LABELS.MODAL_DESCRIPTION,
          element: <PayrollVoucherResponseModal voucher={voucher} />,
        })
      }
      className="cursor-pointer rounded-full transition-all hover:opacity-80"
    >
      {badge}
    </button>
  );
}
